import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Container,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  InputAdornment,
  Fab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import SkeletonLoading from "./skeletonLoading/SkeletonLoading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import useUpdate from "../../../../api/useUpdate";
import { useTranslation } from "react-i18next";
import MedicineOrderPrint from "../../Pharmacy/POS/MedicineOrderPrint";

const CollectMedicineOrderFees = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);

  // Helper function to build query string
  const buildQuery = (search, start, end, dateRange, status) => {
    let q = "";
    if (search) q += `search=${encodeURIComponent(search)}`;

    // Handle custom date range
    if (dateRange === "custom") {
      if (start) q += `${q ? "&" : ""}start_datetime=${start}T00:00:00`;
      if (end) q += `${q ? "&" : ""}end_datetime=${end}T23:59:59`;
    } else {
      // Map frontend date_range to backend type parameter
      const typeMapping = {
        today: "daily",
        week: "weekly",
        month: "monthly",
        year: "yearly",
        custom: "",
      };
      const backendType = typeMapping[dateRange];
      if (backendType) {
        q += `${q ? "&" : ""}type=${backendType}`;
      }
    }
    // Add status filter - only if not "all"
    if (status !== "all") {
      q += `${q ? "&" : ""}payment_status=${status}`;
    }
    return q;
  };

  // Build initial query with default filters (today, all statuses)
  const [query, setQuery] = useState(() => buildQuery("", "", "", "today", "all"));

  // Building query for backend when filters change
  useEffect(() => {
    const newQuery = buildQuery(searchTerm, startDate, endDate, dateRangeFilter, statusFilter);
    setQuery(newQuery);
  }, [searchTerm, startDate, endDate, dateRangeFilter, statusFilter]);

  const {
    data = { results: [], count: 0 },
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchObjects(
    ["medicine-orders", page, rowsPerPage, query],
    `medicine-orders/?${query}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Remove the refetch useEffect - useFetchObjects will handle refetching automatically
  // The refetch on query change was causing the data to disappear

  useEffect(() => {
    setTotalOrders(data.count);
  }, [data.count]);

  const { handleUpdate, isLoading: updateLoading } = useUpdate(
    "medicine-orders",
    token
  );

  const handleStatusChange = (id) => {
    const formData = new FormData();
    formData.append("payment_status", 2);
    handleUpdate(id, formData);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleClearSearch = () => setSearchTerm("");
  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  // Calculate totals for different statuses
  const pageTotal = data.results.reduce((sum, order) => {
    return sum + Number(order.total_price || 0);
  }, 0);

  const paidTotal = data.results
    .filter((o) => o.payment_status === 2)
    .reduce((sum, order) => sum + Number(order.total_price || 0), 0);

  const pendingTotal = data.results
    .filter((o) => o.payment_status === 1)
    .reduce((sum, order) => sum + Number(order.total_price || 0), 0);

  const paidCount = data.results.filter((o) => o.payment_status === 2).length;
  const pendingCount = data.results.filter(
    (o) => o.payment_status === 1
  ).length;
  const pageCount = data.results.length;

  const handlePrintClick = (order) => {
    setPrintData(order);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  if (isLoading) {
    return <SkeletonLoading />;
  }

  if (isError) {
    return <ErrorPage errorMessage={error.message} />;
  }

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 1:
        return t("reception.pending");
      case 2:
        return t("reception.paid");
      case 3:
        return t("reception.canceled");
      default:
        return t("reception.unknown");
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 1:
        return "warning";
      case 2:
        return "success";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {t(
            "reception.collectMedicineOrderFees",
            "Collect Medicine Order Fees"
          )}
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          {t(
            "reception.manageMedicineOrderPayments",
            "Manage medicine order payments and collections"
          )}
        </Typography>

        <Stack
          direction={isSmallScreen ? "column" : "row"}
          gap={2}
          sx={{ width: isSmallScreen ? "100%" : "auto" }}
        >
          <TextField
            fullWidth
            label={t(
              "reception.searchMedicineOrders",
              "Search Medicine Orders"
            )}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          />
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 180,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <CalendarTodayIcon color="primary" />
                </InputAdornment>
              }
            >
              <MenuItem value="today">{t("reception.today", "Today")}</MenuItem>
              <MenuItem value="week">
                {t("reception.thisWeek", "This Week")}
              </MenuItem>
              <MenuItem value="month">
                {t("reception.thisMonth", "This Month")}
              </MenuItem>
              <MenuItem value="year">
                {t("reception.thisYear", "This Year")}
              </MenuItem>
              <MenuItem value="custom">
                {t("reception.customRange", "Custom Range")}
              </MenuItem>
            </Select>
          </FormControl>
          {dateRangeFilter === "custom" && (
            <>
              <TextField
                type="date"
                variant="outlined"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputProps={{
                  endAdornment: startDate && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setStartDate("")} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 180,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
                label={t("reception.startDate", "Start Date")}
              />
              <TextField
                type="date"
                variant="outlined"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputProps={{
                  endAdornment: endDate && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setEndDate("")} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 180,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
                label={t("reception.endDate", "End Date")}
              />
              {(startDate || endDate) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearDates}
                  startIcon={<ClearIcon />}
                  sx={{
                    minWidth: 120,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  {t("reception.clearDates", "Clear Dates")}
                </Button>
              )}
            </>
          )}
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 180,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">
                {t("reception.allStatus", "All Status")}
              </MenuItem>
              <MenuItem value="1">{t("reception.pending", "Pending")}</MenuItem>
              <MenuItem value="2">{t("reception.paid", "Paid")}</MenuItem>
              <MenuItem value="3">
                {t("reception.canceled", "Canceled")}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Grid container spacing={3} padding="20px" mt={3}>
        {data.results.length === 0 ? (
          <Typography>{t("reception.noMatchingMedicine")}</Typography>
        ) : (
          data.results.map((order, index) => {
            const isPaid = order.payment_status === 2;
            return (
              <Grid item xs={12} md={6} key={order.id}>
                <Paper
                  elevation={isPaid ? 2 : 4}
                  sx={{
                    mb: 3,
                    padding: "24px",
                    borderRadius: "12px",
                    width: "100%",
                    boxShadow: isPaid
                      ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
                      : "0px 8px 24px rgba(255, 0, 0, 0.3)",
                    backgroundColor: isPaid ? "#f9f9f9" : "#ffebee",
                    borderLeft: isPaid ? "none" : "4px solid #d32f2f",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                    >
                      {t("reception.medicineOrder")} #{order.id} - {order.date}
                    </Typography>
                    <Box>
                      <Chip
                        label={getPaymentStatusLabel(order.payment_status)}
                        color={getPaymentStatusColor(order.payment_status)}
                        sx={{
                          fontWeight: "bold",
                          fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                        }}
                      />
                      <IconButton
                        color="primary"
                        onClick={() => handlePrintClick(order)}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 2 }}
                  >
                    <Typography
                      fontSize={isSmallScreen ? "0.85rem" : "1rem"}
                      color={!isPaid ? "error.main" : "text.primary"}
                    >
                      <strong>{t("reception.totalPrice")}:</strong>{" "}
                      {order.total_price} ؋
                    </Typography>
                  </Stack>

                  {order.payment_status === 1 && (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <Button
                        variant="contained"
                        fullWidth={isSmallScreen}
                        disabled={updateLoading}
                        startIcon={
                          updateLoading && <CircularProgress size={24} />
                        }
                        onClick={() => handleStatusChange(order.id)}
                        sx={{
                          minWidth: 150,
                          background:
                            "linear-gradient(90deg, #d32f2f, #f44336)",
                          color: "#fff",
                          fontWeight: "bold",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          transition: "0.3s",
                          "&:hover": {
                            background:
                              "linear-gradient(90deg, #f44336, #d32f2f)",
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                          },
                        }}
                      >
                        {updateLoading
                          ? t("reception.loading")
                          : t("reception.collectFees")}
                      </Button>
                    </Stack>
                  )}

                  <Divider sx={{ mt: 2, mb: 2 }} />

                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-content-${order.id}`}
                      id={`panel-header-${order.id}`}
                    >
                      <Typography
                        fontWeight="bold"
                        color={!isPaid ? "error.main" : "text.primary"}
                      >
                        {t("reception.medicineInOrder")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {order.items && order.items.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">
                                  {t("reception.medicine")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.sellPrice")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.quantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.createdAt")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                              {order.items.map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {item.stock_medicine_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.sell_price_display} ؋
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.created_at}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>
                          {t("reception.noMedicineOrder")}
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Grid>
            );
          })
        )}

        {/* Print Component */}
        {printData && (
          <div ref={printRef} style={{ display: "none" }}>
            <MedicineOrderPrint data={printData} />
          </div>
        )}

        {/* Page Total Display */}
        {pageCount > 0 && (
          <Box
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {/* Breakdown */}
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                boxShadow: 3,
                minWidth: 200,
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Breakdown:
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" color="success.main">
                  Paid ({paidCount}):
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="success.main"
                >
                  {paidTotal.toLocaleString()} ؋
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="warning.main">
                  Pending ({pendingCount}):
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="warning.main"
                >
                  {pendingTotal.toLocaleString()} ؋
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={2}>
                {t("reception.rowsPerPage")}:
              </Typography>
              <FormControl variant="standard" sx={{ minWidth: 120 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  label={t("reception.rowsPerPage")}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalOrders / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CollectMedicineOrderFees;
